import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Heart, Check, ChevronDown, ChevronUp, MapPin, ArrowRight, Phone, UtensilsCrossed, Wind } from 'lucide-react';

const AgedCareHealthcareCleaning = ({ onQuoteClick }) => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: "What kind of cleaning do you provide for aged care facilities?",
      a: "Kitchen hygiene cleaning, covering equipment, surfaces and the thorough standard these facilities require, given the vulnerable residents they serve."
    },
    {
      q: "Do you work with aged care providers across WA?",
      a: "We currently provide ongoing kitchen hygiene cleaning for Baptist Care's facilities from Perth through the South West to Albany, and we're actively growing our presence with other aged care and healthcare providers across the state."
    },
    {
      q: "Is this a one-off service or an ongoing arrangement?",
      a: "Ongoing. Our aged care work is built on a sustained relationship, visiting regularly to maintain the hygiene standard these facilities require, not a one-off clean."
    },
    {
      q: "Can you meet the specific hygiene requirements of a healthcare kitchen?",
      a: "Yes, aged care and healthcare kitchens require a higher standard than a typical commercial site, and that's the standard we work to across every visit."
    }
  ];

  // Service Schema for SEO - Perth to Albany via South West
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Aged Care & Healthcare Facility Cleaning",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://www.extreme-clean.com.au/#organization",
      "name": "Extreme Clean Australia"
    },
    "areaServed": [
      "Perth", "Rockingham", "Mandurah", "Bunbury", "Busselton", 
      "Margaret River", "Australind", "Albany"
    ],
    "description": "Kitchen hygiene cleaning for aged care and healthcare facilities from Perth through the South West to Albany. Ongoing cleaning built to the standard vulnerable residents require."
  };

  const relatedServices = [
    {
      title: 'Commercial Kitchen Cleaning',
      description: 'Full kitchen cleaning to a hygiene-first standard.',
      link: '/services/commercial-kitchen-cleaning-perth',
      icon: UtensilsCrossed
    },
    {
      title: 'Canopy, Duct & Rooftop Fan Cleaning',
      description: 'AS 1851:2012 compliant exhaust cleaning for facility kitchens.',
      link: '/services/canopy-duct-fan-cleaning-perth',
      icon: Wind
    }
  ];

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.extreme-clean.com.au/industries/aged-care-healthcare-cleaning-perth" />
        <title>Aged Care & Healthcare Facility Cleaning Perth | Extreme Clean Australia</title>
        <meta name="description" content="Kitchen hygiene cleaning for aged care and healthcare facilities from Perth to Albany. Ongoing cleaning for Baptist Care and growing across WA's aged care sector." />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />

      <div className="pt-24 lg:pt-44" data-testid="aged-care-healthcare-page">
        {/* Hero - Full width background style */}
        <section className="bg-[#0A0A0A] py-24 relative" data-testid="service-hero">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1619267135268-059627071ce8?crop=entropy&cs=srgb&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&ixlib=rb-4.1.0&q=85"
              alt="Aged care facility complex with landscaped grounds"
              className="w-full h-full object-cover opacity-30"
            />
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-[#3B82F6] bg-opacity-20 px-4 py-2 mb-6">
                <Heart size={20} className="text-[#3B82F6]" />
                <span className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Aged Care & Healthcare</span>
              </div>
              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl !leading-[1.2] mb-6 text-white">
                Aged Care & Healthcare Facility Cleaning Perth
              </h1>
              <p className="text-xl text-gray-300 mb-4">
                Kitchen hygiene cleaning built for the standard aged care and healthcare facilities require
              </p>
              <p className="text-gray-400 mb-8">
                Aged care and healthcare kitchens carry a higher standard than most commercial sites, vulnerable residents and patients mean hygiene isn't optional, it's the baseline. Extreme Clean Australia provides ongoing kitchen hygiene cleaning for Baptist Care's facilities from Perth through the South West to Albany, and we're actively growing our presence across other aged care and healthcare providers in WA.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={onQuoteClick}
                  className="bg-[#3B82F6] hover:bg-blue-600 text-white uppercase font-bold tracking-widest px-8 py-4 text-sm transition-all flex items-center justify-center gap-2"
                >
                  Get Free Quote <ArrowRight size={18} />
                </button>
                <a
                  href="tel:0861875523"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0A0A0A] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
                >
                  <Phone size={18} /> Call 08 6187 5523
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Scope</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">What's Included</h2>
              <p className="text-gray-600 mb-8">
                Kitchen hygiene cleaning for aged care and healthcare facility kitchens, covering equipment, surfaces, and the thorough standard these environments require. Scope is built around each facility's specific requirements, recognising that a kitchen serving vulnerable residents needs a different level of attention to a standard commercial site.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'Kitchen equipment cleaning',
                  'Surface sanitisation',
                  'Thorough hygiene standards',
                  'Facility-specific scope',
                  'Regular scheduled visits',
                  'Healthcare-grade cleaning',
                  'Documentation provided',
                  'Compliance-ready service'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check size={20} className="text-[#3B82F6] flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* A Real, Ongoing Relationship */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Partnership</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">A Real, Ongoing Relationship</h2>
              <p className="text-gray-600">
                We provide ongoing kitchen hygiene cleaning across Baptist Care's facilities, from Perth through the South West to Albany. That's not a one-off job, it's a sustained relationship built on consistently meeting the standard aged care kitchens require, visit after visit.
              </p>
            </div>
          </div>
        </section>

        {/* Growing Our Presence */}
        <section className="py-20 bg-[#273268]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Growth</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6 text-white">Growing Our Presence in Aged Care and Healthcare</h2>
              <p className="text-gray-300">
                Baptist Care is our foundation in this space, and we're actively working to grow our presence further across WA's aged care and healthcare sector. This is a genuine growth priority for the business, built on the track record we've already established.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">FAQ</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Common Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index} 
                    className="border border-gray-200 bg-gray-50"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left"
                      aria-expanded={openFaq === index}
                    >
                      <span className="font-heading font-bold text-lg pr-4">{faq.q}</span>
                      {openFaq === index ? (
                        <ChevronUp size={20} className="text-[#3B82F6] flex-shrink-0" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                    <div 
                      className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                    >
                      <div className="px-6 pb-4">
                        <p className="text-gray-600">{faq.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Areas We Service */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-2">
                <MapPin size={20} className="text-[#3B82F6]" />
                <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Service Areas</p>
              </div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Areas We Service</h2>
              <p className="text-gray-600">
                We provide aged care and healthcare facility cleaning from Perth through the South West to Albany, and are growing our presence across WA more broadly.
              </p>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Related</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Related Services</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {relatedServices.map((service, index) => (
                  <Link 
                    key={index}
                    to={service.link}
                    className="block bg-gray-50 p-6 border border-gray-200 hover:border-[#3B82F6] hover:shadow-md transition-all group"
                  >
                    <div className="w-12 h-12 bg-blue-50 mb-4 flex items-center justify-center group-hover:bg-[#3B82F6] transition-colors">
                      <service.icon size={24} className="text-[#3B82F6] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-heading font-bold text-lg mb-2 group-hover:text-[#3B82F6] transition-colors">{service.title}</h3>
                    <p className="text-gray-600 text-sm">{service.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#3B82F6]">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
              Ready to Discuss Your Aged Care Facility Cleaning Needs?
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Get a free quote for aged care and healthcare facility cleaning across WA.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onQuoteClick}
                className="bg-white text-[#0A0A0A] hover:bg-gray-100 transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm"
              >
                Get Free Quote
              </button>
              <a
                href="tel:0861875523"
                className="border-2 border-white text-white hover:bg-white hover:text-[#3B82F6] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
              >
                <Phone size={18} /> Call 08 6187 5523
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AgedCareHealthcareCleaning;
